import { type Action, createStore } from 'react-sweet-state';
import { cloneDeep, uniq } from 'lodash';

import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';
import type { PlaybookTemplateIdea } from 'src/data/performanceInsights';
import { isApplicationModeInfinity } from 'src/data/system';
import {
  isAnywordMultiPromptTemplate,
  type MultiPromptTemplate,
  type Template,
  TEMPLATE_STATUS,
} from 'src/data/template';
import {
  deleteCustomerMultiPromptTemplate,
  deleteReleasedExpertMultiPromptTemplate,
  queryCustomerMultiPromptTemplates,
  releaseExpertMultiPromptTemplate,
  updateExpertMultiPromptTemplate,
} from 'src/graphql/multiPromptTemplates';
import type { ID } from 'src/lib/collection';
import { captureMessage } from 'src/lib/sentry';
import type { CommonContainerProps } from 'src/lib/sweetState';
import type {
  BackendCustomerMultiPromptTemplate,
  BackendMutationUpdateExpertMultiPromptTemplateArgs,
} from 'src/types/backend';

// A template as kept in the store: the domain template plus the multi-prompt tool tag and the
// distinct sub-tools derived from its blocks.
export interface StoredMultiPromptTemplate extends MultiPromptTemplate {
  tool: GenerationTool;
  subTools: GenerationTool[];
  categoryGenerationTool?: GenerationTool;
}

interface MultiPromptTemplateFieldsData {
  titles: Record<string, string>;
  placeholders: Record<string, string | undefined>;
  order: string[];
}

export interface State {
  multiPromptTemplates: StoredMultiPromptTemplate[];
  loadingMultiPromptTemplates: boolean;
  loadedMultiPromptTemplates: boolean;
  // A failed workspace query leaves `loadedMultiPromptTemplates` false, because the workspace's
  // templates stay unknown. A screen that waits for them must tell that apart from a query still
  // in flight. Otherwise it waits for something that never arrives.
  multiPromptTemplatesError: boolean;
  savingMultiPromptTemplates: boolean;
  deletingMultiPromptTemplate: boolean;
  multiPromptBriefExtractionResult: Record<string, unknown> | null;

  customerMultiPromptTemplates: StoredMultiPromptTemplate[];
  loadingCustomerMultiPromptTemplates: boolean;
  shouldPrepopulateCsv: boolean;

  // A template idea picked in Performance Insights, held in memory for the Template Builder to
  // hydrate from. Nothing is persisted here — the builder's own save creates the template.
  sourceTemplateIdea: PlaybookTemplateIdea | null;
}

export type Actions = typeof ACTIONS;

export interface ContainerProps extends CommonContainerProps<State> {
  currentCustomerId: string;
  currentWorkspaceId: string;
}

const preparePossiblyJsonValue = (value: unknown) => {
  if (typeof value === 'string') {
    return JSON.parse(value || 'null');
  }
  return value;
};

const mapMultiPromptTemplate = (
  template: BackendCustomerMultiPromptTemplate
): StoredMultiPromptTemplate => {
  // Assuming template is a response from BE and fields and blocks are strings
  const fields = preparePossiblyJsonValue(template.fields) || [];
  const blocks = preparePossiblyJsonValue(template.blocks) || [];

  const blockTypes = blocks.map((block: { type: GenerationTool }) => block.type);

  // BE's `generationTool` must not keep that key: on a template object the FE already reads
  // `generationTool` as "the tool this template generates with" (always `customMultiPrompt`), while
  // BE means "the tool whose category this template is listed under".
  const { generationTool, ...restTemplate } = template;

  return {
    ...restTemplate,
    fields,
    blocks,
    tool: GENERATION_TOOL.customMultiPrompt,
    subTools: uniq(blockTypes),
    categoryGenerationTool: generationTool ?? undefined,
  } as unknown as StoredMultiPromptTemplate;
};

const mapMultiPromptTemplatesResult = (
  result: BackendCustomerMultiPromptTemplate[] | undefined
): StoredMultiPromptTemplate[] => {
  // A missing payload is a failed query, not an empty list — throw so the caller's catch reports it
  // to Sentry and the already-loaded templates are kept.
  if (!result) {
    throw new Error('Multi-prompt templates query returned no result');
  }
  return result.map(mapMultiPromptTemplate);
};

const STORE_NAME = 'multi-prompt-templates-store';

const INITIAL_STATE: State = {
  multiPromptTemplates: [],
  loadingMultiPromptTemplates: false,
  loadedMultiPromptTemplates: false,
  multiPromptTemplatesError: false,
  savingMultiPromptTemplates: false,
  deletingMultiPromptTemplate: false,
  multiPromptBriefExtractionResult: null,

  customerMultiPromptTemplates: [],
  loadingCustomerMultiPromptTemplates: false,
  shouldPrepopulateCsv: false,
  sourceTemplateIdea: null,
};

/**
 * Private actions
 */
const PRIVATE_ACTIONS = {
  clearStore:
    (): Action<State> =>
    ({ setState }) => {
      setState(cloneDeep(INITIAL_STATE));
    },

  updateTemplate:
    (template: BackendCustomerMultiPromptTemplate | undefined): Action<State> =>
    ({ getState, setState }) => {
      // A missing payload means the mutation failed; throw so the caller reports the failure instead
      // of silently reporting success.
      if (!template) {
        throw new Error('Multi-prompt template mutation returned no template');
      }
      const { multiPromptTemplates } = getState();

      const mappedTemplate = mapMultiPromptTemplate(template);
      const updatedTemplates = multiPromptTemplates.map((existingTemplate) => {
        if (existingTemplate.id === mappedTemplate.id) {
          return { ...existingTemplate, ...mappedTemplate };
        }
        return existingTemplate;
      });

      setState({ multiPromptTemplates: updatedTemplates });
    },
};

/**
 * Public actions
 */
export const ACTIONS = {
  init:
    (customerId: string, workspaceId: string): Action<State> =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.clearStore());

      // we don't need "multi prompt templates" in Infinity
      if (isApplicationModeInfinity()) {
        return;
      }

      if (customerId && workspaceId) {
        dispatch(ACTIONS.queryWorkspaceMultiPromptTemplates({ customerId, workspaceId }));
      }
    },

  update:
    (customerId: string, workspaceId: string): Action<State> =>
    ({ dispatch }) => {
      // we don't need "multi prompt templates" in Infinity
      if (isApplicationModeInfinity()) {
        return;
      }

      if (customerId && workspaceId) {
        dispatch(ACTIONS.queryWorkspaceMultiPromptTemplates({ customerId, workspaceId }));
      }
    },

  getLoadingMultiPromptTemplates:
    (): Action<State, void, boolean> =>
    ({ getState }) => {
      return getState().loadingMultiPromptTemplates;
    },

  queryCustomerMultiPromptTemplates:
    (): Action<State, ContainerProps> =>
    async ({ setState }, { currentCustomerId }) => {
      setState({ loadingCustomerMultiPromptTemplates: true });
      try {
        const result = await queryCustomerMultiPromptTemplates({ customerId: currentCustomerId });
        const mappedResult = mapMultiPromptTemplatesResult(result);
        setState({ customerMultiPromptTemplates: mappedResult });
      } catch (error) {
        // Report "warning" to Sentry
        captureMessage('Error while calling "queryMultiPromptTemplates" GQL query', {
          level: 'warning',
          extra: {
            error: String(error),
            source: 'MultiPromptTemplatesStore -> queryAccountMultiPromptTemplates',
          },
        });
      } finally {
        setState({ loadingCustomerMultiPromptTemplates: false });
      }
    },

  queryWorkspaceMultiPromptTemplates:
    ({ customerId, workspaceId }: { customerId?: string; workspaceId?: string } = {}): Action<
      State,
      ContainerProps
    > =>
    async ({ setState }, { currentCustomerId, currentWorkspaceId }) => {
      setState({ loadingMultiPromptTemplates: true, multiPromptTemplatesError: false });
      try {
        const result = await queryCustomerMultiPromptTemplates({
          customerId: customerId || currentCustomerId,
          workspaceId: workspaceId || currentWorkspaceId,
        });
        const mappedResult = mapMultiPromptTemplatesResult(result);
        // Two of these run at once when a project opens. Clearing the flag here as well as on
        // entry keeps a failure that lands after a success from marking loaded templates broken.
        setState({
          multiPromptTemplates: mappedResult,
          loadedMultiPromptTemplates: true,
          multiPromptTemplatesError: false,
        });
      } catch (error) {
        // Report "warning" to Sentry
        captureMessage('Error while calling "queryCustomerMultiPromptTemplates" GQL query', {
          level: 'warning',
          extra: {
            error: String(error),
            source: 'MultiPromptTemplatesStore -> queryWorkspaceMultiPromptTemplates',
          },
        });
        setState({ multiPromptTemplatesError: true });
      } finally {
        setState({ loadingMultiPromptTemplates: false });
      }
    },

  deleteMultiPromptTemplate:
    (customerId: string, id: string): Action<State> =>
    async ({ setState, getState }) => {
      setState({ deletingMultiPromptTemplate: true });
      try {
        await deleteCustomerMultiPromptTemplate({ customerId, id });
        const multiPromptTemplates = getState().multiPromptTemplates.filter((t) => t.id !== id);
        setState({ multiPromptTemplates });
      } finally {
        setState({ deletingMultiPromptTemplate: false });
      }
    },

  getMultiPromptTemplates:
    (
      options: { includeDraft?: boolean } | null = null
    ): Action<State, void, StoredMultiPromptTemplate[]> =>
    ({ getState }) => {
      const templates = getState().multiPromptTemplates;
      if (!options?.includeDraft) {
        return templates.filter((t) => t.status !== TEMPLATE_STATUS.draft);
      }
      return templates;
    },

  getMultiPromptReleasedTemplates:
    (): Action<State, void, StoredMultiPromptTemplate[]> =>
    ({ getState }) => {
      const { multiPromptTemplates } = getState();
      return multiPromptTemplates.filter((template) =>
        isAnywordMultiPromptTemplate(template as unknown as Template)
      );
    },

  getMultiPromptTemplate:
    (id: ID): Action<State, void, StoredMultiPromptTemplate | undefined> =>
    ({ getState }) => {
      const { multiPromptTemplates } = getState();
      return multiPromptTemplates.find((t) => t.id === id);
    },

  getMultiPromptTemplateProperty:
    (id: ID, prop: keyof StoredMultiPromptTemplate): Action<State, void, unknown> =>
    ({ getState }) => {
      const { multiPromptTemplates } = getState();
      return multiPromptTemplates.find((t) => t.id === id)?.[prop];
    },

  getMultiPromptTemplatesProperty:
    (prop: keyof StoredMultiPromptTemplate): Action<State, void, unknown[]> =>
    ({ getState }) => {
      const { multiPromptTemplates } = getState();
      return multiPromptTemplates.map((t) => t[prop]);
    },

  getMultiPromptTemplateFields:
    (id: ID): Action<State, void, MultiPromptTemplateFieldsData | null> =>
    ({ getState }) => {
      const { multiPromptTemplates } = getState();
      const fields = multiPromptTemplates.find((t) => t.id === id)?.fields as
        | Array<{ name: string; description?: string }>
        | undefined;
      if (!fields) {
        return null;
      }
      const fieldsData: MultiPromptTemplateFieldsData = { titles: {}, placeholders: {}, order: [] };
      fields.forEach((field) => {
        const fieldKey = field.name.toLowerCase().replace(/ /g, '_');
        fieldsData.titles[fieldKey] = field.name;
        fieldsData.placeholders[fieldKey] = field.description;
        fieldsData.order.push(fieldKey);
      });
      return fieldsData;
    },

  getMultiPromptBriefExtractionResultByChainId:
    (chainId: string): Action<State, void, unknown> =>
    ({ getState }) => {
      const { multiPromptBriefExtractionResult } = getState();

      return multiPromptBriefExtractionResult ? multiPromptBriefExtractionResult[chainId] : null;
    },

  clearMultiPromptBriefExtractionResultByChainId:
    (chainId: string): Action<State> =>
    ({ setState, getState }) => {
      const { multiPromptBriefExtractionResult } = getState();

      if (multiPromptBriefExtractionResult) {
        const newMultiPromptBriefExtractionResult = { ...multiPromptBriefExtractionResult };
        delete newMultiPromptBriefExtractionResult[chainId];
        setState({ multiPromptBriefExtractionResult: newMultiPromptBriefExtractionResult });
      }
    },

  setMultiPromptBriefExtractionResultByChainId:
    (chainId: string, briefExtractionResult: unknown): Action<State> =>
    ({ getState, setState }) => {
      const { multiPromptBriefExtractionResult } = getState();
      const newMultiPromptBriefExtractionResult = {
        ...(multiPromptBriefExtractionResult || {}),
        [chainId]: briefExtractionResult,
      };
      setState({ multiPromptBriefExtractionResult: newMultiPromptBriefExtractionResult });
    },

  getMultiPromptTemplateBlocks:
    (id: ID): Action<State, void, Record<string, GenerationTool>> =>
    ({ getState }) => {
      const { multiPromptTemplates } = getState();
      const blocks = multiPromptTemplates.find((t) => t.id === id)?.blocks;
      if (!blocks) {
        return {};
      }
      const blocksData: Record<string, GenerationTool> = {};
      blocks.forEach((block) => {
        blocksData[block.name] = block.type;
      });
      return blocksData;
    },

  updateExpertMultiPromptTemplate:
    (expertTemplateFields: BackendMutationUpdateExpertMultiPromptTemplateArgs): Action<State> =>
    async ({ dispatch }) => {
      const template = await updateExpertMultiPromptTemplate(expertTemplateFields);
      dispatch(PRIVATE_ACTIONS.updateTemplate(template));
    },

  releaseTemplate:
    (id: string): Action<State> =>
    async ({ dispatch }) => {
      const template = await releaseExpertMultiPromptTemplate({
        id,
      });
      dispatch(PRIVATE_ACTIONS.updateTemplate(template));
    },

  deleteReleasedTemplate:
    (id: string): Action<State> =>
    async ({ dispatch }) => {
      const template = await deleteReleasedExpertMultiPromptTemplate({
        id,
      });
      dispatch(PRIVATE_ACTIONS.updateTemplate(template));
    },

  getShouldPrepopulateCsv:
    (): Action<State, void, boolean> =>
    ({ getState }) => {
      return getState().shouldPrepopulateCsv;
    },

  setShouldPrepopulateCsv:
    (shouldPrepopulateCsv: boolean): Action<State> =>
    ({ setState }) => {
      setState({ shouldPrepopulateCsv });
    },
  addMultiPromptTemplate:
    (template: BackendCustomerMultiPromptTemplate): Action<State> =>
    ({ getState, setState }) => {
      const { multiPromptTemplates } = getState();
      const mappedTemplate = mapMultiPromptTemplate(template);
      setState({ multiPromptTemplates: [...multiPromptTemplates, mappedTemplate] });
    },

  setSourceTemplateIdea:
    (idea: PlaybookTemplateIdea): Action<State> =>
    ({ setState }) => {
      setState({ sourceTemplateIdea: idea });
    },

  clearSourceTemplateIdea:
    (): Action<State> =>
    ({ setState }) => {
      setState({ sourceTemplateIdea: null });
    },
};

export const MultiPromptTemplatesStore = createStore<State, Actions>({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: STORE_NAME,
});
